import { NavigateFunction } from 'react-router-dom';
import { EMBED_PARAM_KEYS } from '../hooks/useEmbedParams';

// Embed plumbing params that must NOT leak into a path reported to the FinSuite parent.
// These are FinSuite's own iframe query params (added to the iframe src); reporting them
// back would pollute the parent's external link and URL hash. Includes `isDarkMode`, which
// EMBED_PARAM_KEYS omits because navigation does not need to preserve it.
const REPORTED_PATH_STRIP_KEYS = ['embed', 'source', 'userAlias', 'isDarkMode'] as const;

/**
 * Builds the clean, app-relative path to report to the FinSuite parent via
 * FINSUITE_CURRENT_PATH: `pathname` + `search` (minus embed plumbing params) + `hash`.
 *
 * The result always starts with '/', so it satisfies the parent's relative-path sanitizer.
 *
 * NOTE — the app's own hash is preserved raw (e.g. `/reports/123#row-4`). When FinSuite mirrors
 * this into its own URL hash the result has two `#` (`/app/verity#/reports/123#row-4`). This is
 * intentional: the raw `#` keeps the external "open in new tab" link a real fragment. The
 * parent's hash-restore parser owns splitting on only the first `#`.
 *
 * @param pathname - Current location pathname (always starts with '/')
 * @param search - Current location search string (may include a leading '?')
 * @param hash - Current location hash string (may include a leading '#')
 */
export const buildEmbedReportedPath = (pathname: string, search: string, hash: string): string => {
  const params = new URLSearchParams(search);
  REPORTED_PATH_STRIP_KEYS.forEach((key) => params.delete(key));
  const cleanSearch = params.toString();
  return `${pathname}${cleanSearch ? `?${cleanSearch}` : ''}${hash || ''}`;
};

/**
 * Type guard for a safe, same-document relative path received from the parent (e.g. a
 * FINSUITE_NAVIGATE_TO restore target derived from a user-pasted URL hash). Only a value
 * beginning with a single '/' is allowed — rejects protocol-relative ('//evil.com'),
 * absolute ('https://…'), scheme, and backslash forms before they can reach router navigation.
 */
export const isSafeReportedPath = (path: unknown): path is string =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');

/**
 * Utility function to navigate while preserving embed parameters
 *
 * @param navigate - React Router navigate function
 * @param path - The path to navigate to
 * @param preserveEmbedParams - Whether to preserve embed-related query parameters (default: true)
 * @param options - Navigation options (e.g., replace)
 *
 * @example
 * navigatePreservingParams(navigate, '/admin/users', true, { replace: true });
 */
export const navigatePreservingParams = (
  navigate: NavigateFunction,
  path: string,
  preserveEmbedParams: boolean = true,
  options?: { replace?: boolean }
): void => {
  if (!path) {
    return;
  }

  if (!preserveEmbedParams) {
    navigate(path, options);
    return;
  }

  const finalPath = appendEmbedParamsToPath(path);

  navigate(finalPath, options);
};

/**
 * Utility function to extract embed parameters from current URL
 *
 * @returns URLSearchParams containing only embed parameters
 *
 * @example
 * const embedParams = getEmbedParams();
 * const embedValue = embedParams.get('embed');
 */
export const getEmbedParams = (): URLSearchParams => {
  const currentParams = new URLSearchParams(window.location.search);
  const embedParams = new URLSearchParams();

  EMBED_PARAM_KEYS.forEach((key) => {
    const value = currentParams.get(key);
    if (value !== null) {
      embedParams.set(key, value);
    }
  });

  return embedParams;
};

/**
 * Utility function to get current embed parameters as a query string
 *
 * @returns Query string with embed parameters (without leading ?)
 *
 * @example
 * const queryString = getEmbedParamsQueryString();
 * // Returns: "embed=true&source=dashboard&userAlias=jdoe"
 */
export const getEmbedParamsQueryString = (): string => {
  return getEmbedParams().toString();
};

/**
 * Parses a path into its components (base path, query params, fragment)
 *
 * @param path - The path to parse
 * @returns Object containing basePath, params, and fragment
 *
 * @internal
 */
const parsePath = (path: string): { basePath: string; params: URLSearchParams; fragment: string } => {
  const [pathWithoutFragment, fragment = ''] = path.split('#');
  const [basePath, existingParamsString = ''] = pathWithoutFragment.split('?');
  const params = new URLSearchParams(existingParamsString);

  return { basePath, params, fragment };
};

/**
 * Constructs a path from its components
 *
 * @param basePath - The base path without query params or fragment
 * @param params - URLSearchParams object
 * @param fragment - The fragment (hash) part of the URL
 * @returns Constructed path
 *
 * @internal
 */
const constructPath = (basePath: string, params: URLSearchParams, fragment: string): string => {
  const paramsString = params.toString();
  const queryPart = paramsString ? `?${paramsString}` : '';
  const fragmentPart = fragment ? `#${fragment}` : '';

  return `${basePath}${queryPart}${fragmentPart}`;
};

/**
 * Utility function to append embed parameters to a given path
 *
 * @param path - The base path
 * @returns Path with embed parameters appended
 *
 * @example
 * const newPath = appendEmbedParamsToPath('/admin/users?tab=active');
 * // Returns: "/admin/users?tab=active&embed=true&source=dashboard"
 */
export const appendEmbedParamsToPath = (path: string): string => {
  if (!path) {
    return '';
  }

  const { basePath, params, fragment } = parsePath(path);

  // Get current embed params and merge with existing params
  const embedParams = getEmbedParams();
  EMBED_PARAM_KEYS.forEach((key) => {
    const value = embedParams.get(key);
    if (value !== null) {
      params.set(key, value);
    }
  });

  return constructPath(basePath, params, fragment);
};
